/**
 * Post-metadata-write hand-off for the `['v2-grid', liveId]` React Query
 * cache. Re-fetches the snapshot route past the edge cache (`?fresh=1`)
 * and primes the query store with the result so consuming surfaces
 * (`useGridPage`, lobby drawers, the metadata panel, the edit panel)
 * paint the freshly-saved name / image / socials immediately.
 *
 * Returns `true` when the snapshot was successfully refreshed and
 * written into React Query — callers should fall back to
 * `queryClient.invalidateQueries({ queryKey: ['v2-grid', liveId] })`
 * when this returns `false`. `invalidateQueries` alone risks pulling a
 * stale cached response back over the freshly-saved data because the
 * regular query path is allowed to be served from the browser's HTTP
 * cache for `Cache-Control: max-age=10`.
 *
 * Used by both the creator edit panel
 * (`features/lobby/panels/CreatorGridToolsPanel.tsx`) and the launch
 * wizard (`features/lobby/launch/state/use-launch-grid.ts`) so a
 * just-saved board's metadata shows up the moment the user lands on
 * the board page, with no 30s React-Query staleTime gap.
 */

import type { QueryClient } from '@tanstack/react-query';
import { gridIdToPath } from '@/lib/utils';

export async function refreshGridSnapshot(
  queryClient: QueryClient,
  liveId: string,
): Promise<boolean> {
  try {
    const res = await fetch(`/api/grid/${gridIdToPath(liveId)}?fresh=1`, {
      cache: 'no-store',
    });
    if (!res.ok) return false;
    const data = await res.json();
    queryClient.setQueryData(['v2-grid', liveId], data);
    return true;
  } catch {
    // Non-fatal — caller falls back to `invalidateQueries`, and the
    // regular refetchInterval will eventually pick up the new
    // metadata anyway.
    return false;
  }
}
